import React, { useState, useEffect } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { GitCompare, Download, Trash2, Loader2, Sparkles, X } from 'lucide-react';
import ReactMarkdown from 'react-markdown';

interface ComparisonTestCase {
  type: 'person' | 'peopleByPassage' | 'historicalEventsByBook' | 'place' |
  'archaeologyByPassage' | 'archaeologyByBook' | 'cultureByPassage' |
  'placesByPassage' | 'historicalEventsByPassage';
  name?: string;
  book?: string;
  passage?: string;
  summary?: string;
}

interface ComparisonListItem {
  comparisonId: string;
  dataType: string;
  subject: string;
  timestamp: string;
  modelCount: number;
  models: Array<{
    model: string;
    success: boolean;
    validationPassed?: boolean;
  }>;
}

const AVAILABLE_MODELS = [
  { id: 'gemini-2.5-flash', name: 'Gemini 2.5 Flash' },
  { id: 'gemini-2.5-pro', name: 'Gemini 2.5 Pro' },
  { id: 'gemini-3-flash-preview', name: 'Gemini 3.0 Flash' },
  { id: 'gemini-3-pro-preview', name: 'Gemini 3.0 Pro' },
  { id: 'gemini-3.1-pro-preview', name: 'Gemini 3.1 Pro' }
];

const PRESET_TEST_CASES: ComparisonTestCase[] = [
  // Person data
  { type: 'person', name: 'Luke' },
  { type: 'person', name: 'Paul' },
  { type: 'person', name: 'Pharisees' },

  // Place data
  { type: 'place', name: 'Jerusalem' },
  { type: 'place', name: 'Corinth' },

  // Passage-level data
  { type: 'peopleByPassage', book: 'Acts', passage: '9:1-19', summary: "Paul's conversion" },
  { type: 'peopleByPassage', book: 'Luke', passage: '1:26-38', summary: 'The Annunciation' },
  { type: 'placesByPassage', book: 'Acts', passage: '17:16-34', summary: 'Paul in Athens' },
  { type: 'archaeologyByPassage', book: 'John', passage: '5:1-15', summary: 'Pool of Bethesda healing' },
  { type: 'cultureByPassage', book: 'Matthew', passage: '5:1-12', summary: 'The Beatitudes' },
  { type: 'historicalEventsByPassage', book: 'Luke', passage: '2:1-7', summary: 'Birth of Jesus' },

  // Book-level data
  { type: 'historicalEventsByBook', book: 'Luke' },
  { type: 'historicalEventsByBook', book: 'Romans' },
  { type: 'archaeologyByBook', book: 'Acts' },
];

export default function ModelComparison() {
  const [selectedModels, setSelectedModels] = useState<string[]>([
    'gemini-2.5-flash',
    'gemini-3-flash-preview',
    'gemini-3-pro-preview',
    'gemini-3.1-pro-preview'
  ]);
  const [selectedTestCases, setSelectedTestCases] = useState<number[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const [progress, setProgress] = useState('');
  const [comparisons, setComparisons] = useState<ComparisonListItem[]>([]);
  const [isLoadingList, setIsLoadingList] = useState(false);
  const [analysisResult, setAnalysisResult] = useState<{ id: string; text: string } | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  useEffect(() => {
    loadComparisons();
  }, []);

  const loadComparisons = async () => {
    setIsLoadingList(true);
    try {
      const functions = getFunctions();
      const listFn = httpsCallable(functions, 'listModelComparisons');
      const result = await listFn({});
      const data = result.data as { comparisons: ComparisonListItem[] };
      setComparisons(data.comparisons);
    } catch (error) {
      console.error('Error loading comparisons:', error);
      alert('Failed to load comparisons');
    } finally {
      setIsLoadingList(false);
    }
  };

  const runComparison = async () => {
    if (selectedModels.length === 0) {
      alert('Please select at least one model');
      return;
    }
    if (selectedTestCases.length === 0) {
      alert('Please select at least one test case');
      return;
    }

    setIsRunning(true);
    setProgress('Starting comparison...');

    try {
      const functions = getFunctions();
      const testCases = selectedTestCases.map(i => PRESET_TEST_CASES[i]);

      const runFn = httpsCallable(functions, 'runModelComparison');
      setProgress(`Running ${testCases.length} test case(s) with ${selectedModels.length} model(s)...`);

      const result = await runFn({
        testCases,
        models: selectedModels
      });

      const data = result.data as { success: boolean; comparisons: any[] };

      if (data.success) {
        setProgress(`✅ Completed ${data.comparisons.length} comparison(s)`);
        await loadComparisons();
        setSelectedTestCases([]);
      } else {
        setProgress('❌ Comparison failed');
      }
    } catch (error: any) {
      console.error('Error running comparison:', error);
      setProgress(`❌ Error: ${error.message}`);
    } finally {
      setIsRunning(false);
    }
  };

  const downloadComparison = async (comparisonId: string) => {
    try {
      const functions = getFunctions();
      const getFn = httpsCallable(functions, 'getModelComparison');
      const result = await getFn({ comparisonId });
      const data = result.data as any;

      // Download as JSON file
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${comparisonId}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading comparison:', error);
      alert('Failed to download comparison');
    }
  };

  const deleteComparison = async (comparisonId: string) => {
    if (!confirm(`Delete comparison ${comparisonId}?`)) {
      return;
    }

    try {
      const functions = getFunctions();
      const deleteFn = httpsCallable(functions, 'deleteModelComparison');
      await deleteFn({ comparisonId });
      await loadComparisons();
    } catch (error) {
      console.error('Error deleting comparison:', error);
      alert('Failed to delete comparison');
    }
  };

  const [analyzingId, setAnalyzingId] = useState<string | null>(null);

  const analyzeComparison = async (comparisonId: string) => {
    setAnalyzingId(comparisonId);
    setIsAnalyzing(true);
    setAnalysisResult(null);
    try {
      const functions = getFunctions();
      const analyzeFn = httpsCallable(functions, 'analyzeModelComparison');
      const result = await analyzeFn({ comparisonId });
      const data = result.data as { analysis: string };
      setAnalysisResult({ id: comparisonId, text: data.analysis });
    } catch (error: any) {
      console.error('Error analyzing comparison:', error);
      alert(`Failed to analyze comparison: ${error.message}`);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const toggleModel = (modelId: string) => {
    setSelectedModels(prev =>
      prev.includes(modelId)
        ? prev.filter(id => id !== modelId)
        : [...prev, modelId]
    );
  };

  const toggleTestCase = (index: number) => {
    setSelectedTestCases(prev =>
      prev.includes(index)
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  const selectAllTestCases = () => {
    setSelectedTestCases(PRESET_TEST_CASES.map((_, i) => i));
  };

  const clearTestCases = () => {
    setSelectedTestCases([]);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <GitCompare className="w-5 h-5" />
          Model Comparison
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Compare AI model outputs for Person, Place, and contextual data generation.
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Setup Section */}
        <div className="bg-muted p-5 rounded-lg space-y-6">
          {/* Model Selection */}
          <div>
            <h3 className="text-base font-semibold mb-3">Select Models</h3>
            <div className="flex flex-col gap-2">
              {AVAILABLE_MODELS.map(model => (
                <label
                  key={model.id}
                  className="flex items-center gap-2 cursor-pointer p-2 rounded hover:bg-background/50 transition-colors"
                >
                  <input
                    type="checkbox"
                    checked={selectedModels.includes(model.id)}
                    onChange={() => toggleModel(model.id)}
                    disabled={isRunning}
                    className="cursor-pointer"
                  />
                  <span className="text-sm">{model.name}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Test Case Selection */}
          <div>
            <h3 className="text-base font-semibold mb-3">Select Test Cases</h3>
            <div className="flex gap-2 mb-3">
              <Button
                size="sm"
                variant="outline"
                onClick={selectAllTestCases}
                disabled={isRunning}
              >
                Select All
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={clearTestCases}
                disabled={isRunning}
              >
                Clear All
              </Button>
            </div>
            <div className="flex flex-col gap-2">
              {PRESET_TEST_CASES.map((testCase, index) => (
                <label
                  key={index}
                  className="flex items-center gap-2 cursor-pointer p-2 rounded hover:bg-background/50 transition-colors"
                >
                  <input
                    type="checkbox"
                    checked={selectedTestCases.includes(index)}
                    onChange={() => toggleTestCase(index)}
                    disabled={isRunning}
                    className="cursor-pointer"
                  />
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant="secondary" className="text-xs">
                      {testCase.type}
                    </Badge>
                    {testCase.name && <span className="text-sm font-medium">{testCase.name}</span>}
                    {testCase.book && testCase.passage && (
                      <span className="text-sm font-medium">{testCase.book} {testCase.passage}</span>
                    )}
                    {testCase.book && !testCase.passage && (
                      <span className="text-sm font-medium">{testCase.book}</span>
                    )}
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Run Button */}
          <div>
            <Button
              onClick={runComparison}
              disabled={isRunning || selectedModels.length === 0 || selectedTestCases.length === 0}
              className="w-full sm:w-auto"
            >
              {isRunning && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isRunning ? 'Running...' : 'Run Comparison'}
            </Button>
            {progress && (
              <div className="mt-3 p-3 bg-background rounded border-l-4 border-blue-500 text-sm">
                {progress}
              </div>
            )}
          </div>
        </div>

        {/* Results Section */}
        <div>
          <h3 className="text-base font-semibold mb-3">Previous Comparisons</h3>
          {isLoadingList ? (
            <div className="p-10 text-center text-muted-foreground bg-muted rounded-lg">
              <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
              Loading comparisons...
            </div>
          ) : comparisons.length === 0 ? (
            <div className="p-10 text-center text-muted-foreground bg-muted rounded-lg">
              No comparisons yet
            </div>
          ) : (
            <div className="bg-background rounded-lg border overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="bg-muted border-b">
                    <th className="p-3 text-left text-sm font-semibold">Subject</th>
                    <th className="p-3 text-left text-sm font-semibold">Type</th>
                    <th className="p-3 text-left text-sm font-semibold">Date</th>
                    <th className="p-3 text-left text-sm font-semibold">Models</th>
                    <th className="p-3 text-left text-sm font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {comparisons.map(comp => (
                    <React.Fragment key={comp.comparisonId}>
                      <tr className="border-b last:border-b-0 hover:bg-muted/50">
                        <td className="p-3 text-sm">{comp.subject}</td>
                        <td className="p-3 text-sm">
                          <Badge variant="outline" className="text-xs">
                            {comp.dataType}
                          </Badge>
                        </td>
                        <td className="p-3 text-sm text-muted-foreground">
                          {new Date(comp.timestamp).toLocaleString()}
                        </td>
                        <td className="p-3">
                          <div className="flex gap-1.5 flex-wrap">
                            {comp.models.map((m, i) => (
                              <Badge
                                key={i}
                                variant={m.success ? "default" : "destructive"}
                                className={`text-xs ${m.validationPassed
                                    ? 'border-2 border-green-500'
                                    : m.validationPassed === false
                                      ? 'border-2 border-red-500'
                                      : ''
                                  }`}
                                title={`${m.model}: ${m.success ? 'Success' : 'Failed'}${m.validationPassed !== undefined ? `, Validation: ${m.validationPassed ? 'Passed' : 'Failed'}` : ''}`}
                              >
                                {m.model.replace('gemini-', '').replace('-exp', '').replace('-preview', '')}
                              </Badge>
                            ))}
                          </div>
                        </td>
                        <td className="p-3">
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="default"
                              onClick={() => analyzeComparison(comp.comparisonId)}
                              disabled={isAnalyzing}
                              className="bg-purple-600 hover:bg-purple-700 text-white"
                            >
                              {isAnalyzing && analyzingId === comp.comparisonId ? (
                                <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                              ) : (
                                <Sparkles className="h-3 w-3 mr-1" />
                              )}
                              Analyze
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => downloadComparison(comp.comparisonId)}
                            >
                              <Download className="h-3 w-3 mr-1" />
                              Download
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => deleteComparison(comp.comparisonId)}
                              className="text-destructive hover:bg-destructive hover:text-destructive-foreground"
                            >
                              <Trash2 className="h-3 w-3 mr-1" />
                              Delete
                            </Button>
                          </div>
                        </td>
                      </tr>
                      {analysisResult?.id === comp.comparisonId && (
                        <tr className="bg-purple-50 dark:bg-purple-900/10">
                          <td colSpan={5} className="p-4">
                            <div className="relative">
                              <Button
                                size="sm"
                                variant="ghost"
                                className="absolute top-0 right-0 h-6 w-6 p-0"
                                onClick={() => setAnalysisResult(null)}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                              <h4 className="font-semibold mb-2 flex items-center gap-2">
                                <Sparkles className="h-4 w-4 text-purple-600" />
                                AI Analysis
                              </h4>
                              <div className="prose prose-sm dark:prose-invert max-w-none">
                                <ReactMarkdown>{analysisResult.text}</ReactMarkdown>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

